"use client";

import { useState, useEffect, useCallback, useRef, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { downloadCertificate, getDayOfWeek, formatTime } from "@/components/ui/games/GameCertificate";
import { OtherGames } from "@/components/ui/games/OtherGames";
import JSConfetti from "js-confetti";
import { ChevronLeft, ChevronRight, RotateCw, Check } from "lucide-react";

interface Clue {
  number: number;
  clue: string;
  answer: string;
  row: number;
  col: number;
  length: number;
}

interface CrosswordData {
  id: number;
  date: string;
  size: number;
  grid: string[][];
  clues: {
    across: Clue[];
    down: Clue[];
  };
}

function MiniCrosswordContent() {
  const searchParams = useSearchParams();
  const isClubhouse = searchParams?.get("id") === "clubhouse";

  // Game states
  const [playerName, setPlayerName] = useState<string>("");
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [crosswordData, setCrosswordData] = useState<CrosswordData | null>(null);
  const [userGrid, setUserGrid] = useState<string[][]>([]);
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const [direction, setDirection] = useState<"across" | "down">("across");
  const [gameState, setGameState] = useState<"playing" | "won">("playing");
  const [startTime, setStartTime] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [shareMessage, setShareMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const gridRef = useRef<HTMLDivElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  // Load crossword data
  useEffect(() => {
    const loadCrossword = async () => {
      try {
        const { default: crosswords } = await import("./crossword-data.json");
        // For now, just use today's date to pick a puzzle
        const today = new Date();
        const dayIndex = today.getDate() % crosswords.length;
        setCrosswordData(crosswords[dayIndex]);

        // Initialize empty user grid
        const emptyGrid = crosswords[dayIndex].grid.map((row: string[]) =>
          row.map((cell: string) => (cell === "#" ? "#" : ""))
        );
        setUserGrid(emptyGrid);
      } catch (error) {
        console.error("Error loading crossword:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCrossword();
  }, []);

  // Timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (startTime && gameState === "playing") {
      interval = setInterval(() => {
        setCurrentTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [startTime, gameState]);

  const startGame = () => {
    if (playerName.trim().length < 2) {
      setErrorMessage("Please enter your name (at least 2 characters)");
      return;
    }
    setGameStarted(true);
    setStartTime(Date.now());
    setErrorMessage("");
    // Select first cell
    if (crosswordData) {
      for (let r = 0; r < crosswordData.size; r++) {
        for (let c = 0; c < crosswordData.size; c++) {
          if (crosswordData.grid[r][c] !== "#") {
            setSelectedCell({ row: r, col: c });
            return;
          }
        }
      }
    }
  };

  const getCellNumber = (row: number, col: number): number | null => {
    if (!crosswordData) return null;

    // Check if this cell starts any clue
    const acrossClue = crosswordData.clues.across.find((c) => c.row === row && c.col === col);
    const downClue = crosswordData.clues.down.find((c) => c.row === row && c.col === col);

    if (acrossClue) return acrossClue.number;
    if (downClue) return downClue.number;

    return null;
  };

  const getCurrentClue = (): Clue | null => {
    if (!selectedCell || !crosswordData) return null;

    const { row, col } = selectedCell;
    const clues = direction === "across" ? crosswordData.clues.across : crosswordData.clues.down;

    return (
      clues.find((clue) => {
        if (direction === "across") {
          return clue.row === row && col >= clue.col && col < clue.col + clue.length;
        } else {
          return clue.col === col && row >= clue.row && row < clue.row + clue.length;
        }
      }) || null
    );
  };

  const checkCompletion = useCallback(
    (grid: string[][]) => {
      if (!crosswordData) return;

      let isComplete = true;
      let hasErrors = false;
      const newErrors = new Set<string>();

      for (let r = 0; r < crosswordData.size; r++) {
        for (let c = 0; c < crosswordData.size; c++) {
          if (crosswordData.grid[r][c] !== "#") {
            if (grid[r][c] === "") {
              isComplete = false;
            } else if (grid[r][c] !== crosswordData.grid[r][c]) {
              hasErrors = true;
              if (showErrors) {
                newErrors.add(`${r}-${c}`);
              }
            }
          }
        }
      }

      setErrors(newErrors);

      if (isComplete && !hasErrors) {
        setGameState("won");
        const jsConfetti = new JSConfetti();
        jsConfetti.addConfetti();
      }
    },
    [crosswordData, showErrors]
  );

  const handleCellClick = (row: number, col: number) => {
    if (!crosswordData || crosswordData.grid[row][col] === "#") return;

    if (selectedCell?.row === row && selectedCell?.col === col) {
      // Toggle direction if clicking same cell
      setDirection((d) => (d === "across" ? "down" : "across"));
    } else {
      setSelectedCell({ row, col });
    }

    // Focus the hidden input to trigger mobile keyboard
    if (mobileInputRef.current) {
      mobileInputRef.current.focus();
    }
  };

  const handleMobileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedCell || !crosswordData || gameState !== "playing") return;

      const value = e.target.value;

      // Only process single character
      if (value && value.match(/^[a-zA-Z]$/)) {
        const { row, col } = selectedCell;
        const newGrid = [...userGrid];
        newGrid[row][col] = value.toUpperCase();
        setUserGrid(newGrid);

        // Check if puzzle is complete
        checkCompletion(newGrid);

        // Move to next cell
        if (direction === "across" && col < crosswordData.size - 1) {
          let newCol = col + 1;
          while (newCol < crosswordData.size && crosswordData.grid[row][newCol] === "#") newCol++;
          if (newCol < crosswordData.size) setSelectedCell({ row, col: newCol });
        } else if (direction === "down" && row < crosswordData.size - 1) {
          let newRow = row + 1;
          while (newRow < crosswordData.size && crosswordData.grid[newRow][col] === "#") newRow++;
          if (newRow < crosswordData.size) setSelectedCell({ row: newRow, col });
        }
      }

      // Always clear the input
      e.target.value = "";
    },
    [selectedCell, crosswordData, direction, userGrid, gameState, checkCompletion]
  );

  const handleMobileKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!selectedCell || !crosswordData || gameState !== "playing") return;

      const { row, col } = selectedCell;

      // Only handle control keys, not letters
      if (e.key === "Backspace") {
        e.preventDefault();
        // Clear current cell and move back
        const newGrid = [...userGrid];
        newGrid[row][col] = "";
        setUserGrid(newGrid);

        // Move to previous cell
        if (direction === "across" && col > 0 && crosswordData.grid[row][col - 1] !== "#") {
          setSelectedCell({ row, col: col - 1 });
        } else if (direction === "down" && row > 0 && crosswordData.grid[row - 1][col] !== "#") {
          setSelectedCell({ row: row - 1, col });
        }

        // Clear the input
        if (mobileInputRef.current) {
          mobileInputRef.current.value = "";
        }
      }
    },
    [selectedCell, crosswordData, direction, userGrid, gameState]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent | React.KeyboardEvent) => {
      if (!selectedCell || !crosswordData || gameState !== "playing") return;

      const { row, col } = selectedCell;

      if (e.key === "Tab") {
        e.preventDefault();
        setDirection((d) => (d === "across" ? "down" : "across"));
        return;
      }

      if (e.key === "Backspace") {
        e.preventDefault();
        // Clear current cell and move back
        const newGrid = [...userGrid];
        newGrid[row][col] = "";
        setUserGrid(newGrid);

        // Move to previous cell
        if (direction === "across" && col > 0 && crosswordData.grid[row][col - 1] !== "#") {
          setSelectedCell({ row, col: col - 1 });
        } else if (direction === "down" && row > 0 && crosswordData.grid[row - 1][col] !== "#") {
          setSelectedCell({ row: row - 1, col });
        }

        // Clear mobile input
        if (mobileInputRef.current) {
          mobileInputRef.current.value = "";
        }
        return;
      }

      if (e.key === "ArrowUp" && row > 0) {
        let newRow = row - 1;
        while (newRow >= 0 && crosswordData.grid[newRow][col] === "#") newRow--;
        if (newRow >= 0) setSelectedCell({ row: newRow, col });
      } else if (e.key === "ArrowDown" && row < crosswordData.size - 1) {
        let newRow = row + 1;
        while (newRow < crosswordData.size && crosswordData.grid[newRow][col] === "#") newRow++;
        if (newRow < crosswordData.size) setSelectedCell({ row: newRow, col });
      } else if (e.key === "ArrowLeft" && col > 0) {
        let newCol = col - 1;
        while (newCol >= 0 && crosswordData.grid[row][newCol] === "#") newCol--;
        if (newCol >= 0) setSelectedCell({ row, col: newCol });
      } else if (e.key === "ArrowRight" && col < crosswordData.size - 1) {
        let newCol = col + 1;
        while (newCol < crosswordData.size && crosswordData.grid[row][newCol] === "#") newCol++;
        if (newCol < crosswordData.size) setSelectedCell({ row, col: newCol });
      }

      // Letter input - only process if NOT coming from the hidden input
      // The hidden input will handle its own letter processing
      const target = e.target as HTMLElement;
      const isFromHiddenInput = target === mobileInputRef.current;

      if (!isFromHiddenInput && e.key.length === 1 && e.key.match(/[a-zA-Z]/)) {
        const newGrid = [...userGrid];
        newGrid[row][col] = e.key.toUpperCase();
        setUserGrid(newGrid);

        // Check if puzzle is complete
        checkCompletion(newGrid);

        // Move to next cell
        if (direction === "across" && col < crosswordData.size - 1) {
          let newCol = col + 1;
          while (newCol < crosswordData.size && crosswordData.grid[row][newCol] === "#") newCol++;
          if (newCol < crosswordData.size) setSelectedCell({ row, col: newCol });
        } else if (direction === "down" && row < crosswordData.size - 1) {
          let newRow = row + 1;
          while (newRow < crosswordData.size && crosswordData.grid[newRow][col] === "#") newRow++;
          if (newRow < crosswordData.size) setSelectedCell({ row: newRow, col });
        }
      }
    },
    [selectedCell, crosswordData, direction, userGrid, gameState, checkCompletion]
  );

  useEffect(() => {
    if (gameStarted) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [handleKeyDown, gameStarted]);

  // Focus mobile input when cell changes
  useEffect(() => {
    if (selectedCell && mobileInputRef.current && gameStarted) {
      mobileInputRef.current.value = "";
      mobileInputRef.current.focus();
    }
  }, [selectedCell, gameStarted]);

  const toggleShowErrors = () => {
    setShowErrors(!showErrors);
    if (!showErrors) {
      checkCompletion(userGrid);
    } else {
      setErrors(new Set());
    }
  };

  const clearPuzzle = () => {
    if (!crosswordData) return;
    const emptyGrid = crosswordData.grid.map((row) => row.map((cell) => (cell === "#" ? "#" : "")));
    setUserGrid(emptyGrid);
    setErrors(new Set());
  };

  const revealPuzzle = () => {
    if (!crosswordData) return;
    setUserGrid(crosswordData.grid.map((row) => [...row]));
    setGameState("won");
  };

  const handleDownloadCertificate = async () => {
    await downloadCertificate(
      {
        gameName: "Mini Crossword",
        playerName: playerName,
        stats: [getDayOfWeek(), formatTime(currentTime), "Puzzle Completed"],
        subtitle: "Crossword Champion!",
        backgroundColor: { start: "#10b981", end: "#059669" },
      },
      `crossword-${playerName}-${getDayOfWeek().toLowerCase()}.png`
    );
  };

  const generateShareText = useCallback(() => {
    const today = new Date();
    const dateString = today.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    let shareText = `Mini Crossword\n${dateString}\n`;
    shareText += `⏱️ ${formatTime(currentTime)}\n`;
    shareText += `✅ Completed!\n\n`;
    shareText += `Play at nowadays.ai/games/crossword`;

    return shareText;
  }, [currentTime]);

  const handleShare = async () => {
    const shareText = generateShareText();

    try {
      await navigator.clipboard.writeText(shareText);
      setShareMessage("Results copied to clipboard!");
      setTimeout(() => setShareMessage(""), 3000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      setShareMessage("Failed to copy to clipboard");
      setTimeout(() => setShareMessage(""), 3000);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
        {isClubhouse && <img src="/landing/clubhouse.png" alt="Clubhouse" className="h-20 w-auto mb-8" />}
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4 text-black">Mini Crossword</h1>
          <p className="text-base text-gray-600">Loading today's puzzle...</p>
        </div>
      </div>
    );
  }

  // Start screen
  if (!gameStarted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
        {isClubhouse && <img src="/landing/clubhouse.png" alt="Clubhouse" className="h-20 w-auto mb-8" />}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-black">Mini Crossword</h1>
          <p className="text-lg text-gray-600 mb-2">Complete the mini crossword puzzle</p>
          <p className="text-base text-gray-500">{getDayOfWeek()}</p>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="space-y-6">
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="w-full px-6 py-4 text-base border-2 border-green-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              maxLength={20}
              onKeyDown={(e) => e.key === "Enter" && startGame()}
              autoFocus
            />

            {errorMessage && <div className="text-red-600 text-center font-medium">{errorMessage}</div>}

            <button
              onClick={startGame}
              className="w-full bg-green-100 hover:bg-green-200 text-green-700 py-3 text-base rounded-xl font-bold transition-all transform hover:scale-105 shadow-sm"
            >
              Start Playing →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main game
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
      {isClubhouse && <img src="/landing/clubhouse.png" alt="Clubhouse" className="h-20 w-auto mb-6" />}
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-4xl w-full mx-4">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-3 text-black">Mini Crossword</h1>
          <p className="text-base text-gray-600">Hello {playerName}! Complete the puzzle</p>

          {/* Timer */}
          <div className="text-lg font-semibold text-gray-700 mt-2">Time: {formatTime(currentTime)}</div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Crossword Grid */}
          <div className="flex flex-col items-center">
            {/* Hidden input for mobile keyboard */}
            <input
              ref={mobileInputRef}
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              onChange={handleMobileInput}
              onKeyDown={handleMobileKeyDown}
              className="absolute opacity-0 pointer-events-none"
              aria-hidden="true"
              maxLength={1}
            />
            <div
              ref={gridRef}
              className="grid grid-cols-5 gap-0 border-2 border-black bg-white"
              style={{ width: "300px", height: "300px" }}
            >
              {crosswordData &&
                userGrid.map((row, r) =>
                  row.map((cell, c) => {
                    const isBlack = crosswordData.grid[r][c] === "#";
                    const isSelected = selectedCell?.row === r && selectedCell?.col === c;
                    const isInCurrentClue = (() => {
                      const currentClue = getCurrentClue();
                      if (!currentClue) return false;

                      if (direction === "across") {
                        return (
                          r === currentClue.row && c >= currentClue.col && c < currentClue.col + currentClue.length
                        );
                      } else {
                        return (
                          c === currentClue.col && r >= currentClue.row && r < currentClue.row + currentClue.length
                        );
                      }
                    })();
                    const hasError = showErrors && errors.has(`${r}-${c}`);
                    const number = getCellNumber(r, c);

                    return (
                      <div
                        key={`${r}-${c}`}
                        className={`
                        relative border border-gray-300 flex items-center justify-center cursor-pointer
                        transition-all duration-150
                        ${isBlack ? "bg-black" : ""}
                        ${isSelected ? "bg-yellow-200" : ""}
                        ${isInCurrentClue && !isSelected ? "bg-blue-50" : ""}
                        ${hasError ? "bg-red-100" : ""}
                        ${!isBlack ? "hover:bg-gray-50" : ""}
                      `}
                        style={{ width: "60px", height: "60px" }}
                        onClick={() => !isBlack && handleCellClick(r, c)}
                      >
                        {number && <span className="absolute top-0 left-1 text-xs font-bold">{number}</span>}
                        {!isBlack && (
                          <span className={`text-2xl font-bold ${hasError ? "text-red-600" : ""}`}>{cell}</span>
                        )}
                      </div>
                    );
                  })
                )}
            </div>

            {/* Controls */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={clearPuzzle}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all flex items-center gap-2"
              >
                <RotateCw className="w-4 h-4" />
                Clear
              </button>
              <button
                onClick={toggleShowErrors}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  showErrors
                    ? "bg-red-100 hover:bg-red-200 text-red-700"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                <Check className="w-4 h-4" />
                {showErrors ? "Hide Errors" : "Check"}
              </button>
              {process.env.NODE_ENV === "development" && (
                <button
                  onClick={revealPuzzle}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all"
                >
                  Reveal
                </button>
              )}
            </div>
          </div>

          {/* Clues */}
          <div className="space-y-6">
            <div>
              <h3 className="font-bold text-lg mb-3">Across</h3>
              <div className="space-y-2">
                {crosswordData?.clues.across.map((clue) => {
                  const isActive = getCurrentClue()?.number === clue.number && direction === "across";
                  return (
                    <div
                      key={`across-${clue.number}`}
                      className={`p-2 rounded cursor-pointer transition-all ${
                        isActive ? "bg-yellow-100 font-semibold" : "hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        setSelectedCell({ row: clue.row, col: clue.col });
                        setDirection("across");
                      }}
                    >
                      <span className="font-bold mr-2">{clue.number}.</span>
                      {clue.clue}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-3">Down</h3>
              <div className="space-y-2">
                {crosswordData?.clues.down.map((clue) => {
                  const isActive = getCurrentClue()?.number === clue.number && direction === "down";
                  return (
                    <div
                      key={`down-${clue.number}`}
                      className={`p-2 rounded cursor-pointer transition-all ${
                        isActive ? "bg-yellow-100 font-semibold" : "hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        setSelectedCell({ row: clue.row, col: clue.col });
                        setDirection("down");
                      }}
                    >
                      <span className="font-bold mr-2">{clue.number}.</span>
                      {clue.clue}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {gameState === "won" && (
          <div className="mt-6 text-center">
            <div className="text-green-600 font-bold text-xl mb-2">Congratulations! Puzzle Complete!</div>
            <div className="text-gray-600">Time: {formatTime(currentTime)}</div>
          </div>
        )}

        {/* Share and Certificate Section */}
        {gameState === "won" && (
          <>
            <div className="mt-6 mb-8 text-center">
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleShare}
                  className="bg-green-100 hover:bg-green-200 text-green-700 py-3 px-6 rounded-xl font-semibold text-base transition-all transform hover:scale-105 shadow-sm"
                >
                  Share Results
                </button>
                <button
                  onClick={handleDownloadCertificate}
                  className="bg-green-100 hover:bg-green-200 text-green-700 py-3 px-6 rounded-xl font-semibold text-base transition-all transform hover:scale-105 shadow-sm"
                >
                  Download Certificate
                </button>
              </div>
              {shareMessage && <div className="mt-3 text-green-600 font-medium text-sm">{shareMessage}</div>}
            </div>

            {/* Other Games Section */}
            <OtherGames currentGameId="crossword" />
          </>
        )}
      </div>
    </div>
  );
}

export default function MiniCrosswordPage() {
  return (
    <Suspense
      fallback={
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-8 max-w-md w-full mx-4 text-center">
            <h1 className="text-2xl font-bold mb-4">Mini Crossword</h1>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      }
    >
      <MiniCrosswordContent />
    </Suspense>
  );
}
